import React, { useEffect, useState, useRef, useReducer } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  PatternlibButton,
  PatternlibOverlayContainer,
  PatternlibIcon,
  PatternlibSpinner,
  PatternlibTextlink,
} from "@liebherr/patternlib-react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import FormTextinput from "../form-textinput/FormTextinput";
import FormTextarea from "../form-textarea/FormTextarea";
import FormCheckbox from "../form-checkbox/FormCheckbox";
import FormDatepicker from "../form-datepicker/FormDatepicker";
import FormDropdown from "../form-dropdown/FormDropdown";
import Separator from "../separator/Separator";
import styles from "./SendButton.module.css";
import { useLocalStorage, pushToDataLayer } from "../../helpers/helpers";
import { countriesDe } from "./countries_de.js";
import { countriesEn } from "./countries_en.js";
import { useAuth } from "react-oidc-context";

// Remove the user
const removeUser = (removeUser, user, setFieldValue) => {
  removeUser();
  setFieldValue("firstName", user["firstName"] ?? "");
  setFieldValue("lastName", user["lastName"] ?? "");
  setFieldValue("email", user["email"] ?? "");
  setFieldValue("phone", user["phone"] ?? "");
  setFieldValue("company", user["company"] ?? "");
  setFieldValue("zip", user["zip"] ?? "");
  setFieldValue("city", user["city"] ?? "");
  setFieldValue("street", user["street"] ?? "");
  setFieldValue("country", user["country"] ?? "");
};

const SendButton = ({
  part,
  data,
  onError,
  productInformation,
  showModal,
  locationState,
  userData,
}) => {
  const { t, i18n } = useTranslation();

  const [isSubmit, setIsSubmit] = useState(false);
  const [isRequired, setIsRquired] = useState(false);
  const [values, setValues] = useState(null);
  const [show, setShow] = useState(showModal);
  const [user, setUser] = useLocalStorage("user", {});
  const [isConsentSet, setIsConsentSet] = useState(false);
  const overlayRef = useRef();
  const location = useLocation();

  const [latestConfig, setLatestConfig] = useLocalStorage("key", null);
  const auth = useAuth(); // get auth status and user

  // the useRef Hook allows to persist data between renders
  const partRef = useRef();
  const dataRef = useRef();

  // useNavigate Hook to navigate to different pages
  const navigate = useNavigate();

  const configRequestSentGA = (productKey) => {
    const eventName = "configuration_request_sent";
    const pagePath = location.pathname;
    const pageType = "configurator";
    const language =
      i18n.language === "de-DE" || i18n.language === "de" ? "de-DE" : "en-EN";
    const product_key = productKey;
    let props = { pagePath, pageType, language, product_key };

    pushToDataLayer(eventName, props);
  };

  const offerRequestSentGA = (
    applicationName,
    quantityDelivery,
    seriesDelivery,
    quantityYearly
  ) => {
    const eventName = "offer_request_sent";
    const pagePath = location.pathname;
    const pageType = "configurator";
    const language =
      i18n.language === "de-DE" || i18n.language === "de" ? "de-DE" : "en-EN";
    const application_name = applicationName;
    const quantity_delivery = quantityDelivery;
    const series_delivery = seriesDelivery;
    const quantity_yearly = quantityYearly;
    let props = {
      pagePath,
      pageType,
      language,
      application_name,
      quantity_delivery,
      series_delivery,
      quantity_yearly,
    };

    pushToDataLayer(eventName, props);
  };

  // function to change active state of sendButton and displaying a spinner when sending mails
  const reducer = (state, action) => {
    if (action.type === "toggleMailIsPending") {
      return {
        ...state,
        disableConfirmButton: !state.disableConfirmButton,
        showSpinner: !state.showSpinner,
      };
    }
    return state;
  };

  // useReducer to toggle the sendButton disabled and a spinner displayed (while sending mails)
  const [state, dispatch] = useReducer(reducer, {
    disableConfirmButton: false,
    showSpinner: false,
  });

  const GetValuesFromFormik = () => {
    // grab values and submitForm from context
    const { values, submitForm, isValid, setFieldValue } = useFormikContext();

    if (isValid) {
      setIsRquired(false);
    }

    // listen to changes to isSubmit state
    useEffect(() => {
      if (isSubmit) {
        if (!isValid) {
          setIsRquired(true);
        }
        submitForm();
      }
    }, [isSubmit]);

    // If user data is available, set field values (override inital values)
    useEffect(() => {
      if (userData && auth.isAuthenticated) {
        setFieldValue("firstName", userData.me["firstName"]);
        setFieldValue("lastName", userData.me["lastName"]);
        setFieldValue("email", userData.me["mail"]);
        setFieldValue("phone", userData.me.company["telephoneNumber"]);
        setFieldValue("company", userData.me.company["companyName"]);
        setFieldValue("zip", userData.me.company["postalCode"]);
        setFieldValue("city", userData.me.company["locality"]);
        setFieldValue("street", userData.me.company["street"]);
        setFieldValue("country", userData.me.company["country"]);
      }
    }, [userData]);

    return null;
  };

  const ValidationSchema = Yup.object().shape({
    firstName: Yup.string()
      .min(2, t("SendButton.tooShort"))
      .required(t("SendButton.required")),
    lastName: Yup.string()
      .min(2, t("SendButton.tooShort"))
      .required(t("SendButton.required")),
    company: Yup.string().required(t("SendButton.required")),
    email: Yup.string()
      .email(t("SendButton.validEmail"))
      .required(t("SendButton.required")),
    phone: Yup.string()
      .min(2, t("SendButton.tooShort"))
      .required(t("SendButton.required")),
    zip: Yup.string().required(t("SendButton.required")),
    city: Yup.string().required(t("SendButton.required")),
    street: Yup.string().required(t("SendButton.required")),
    country: Yup.string().required(t("SendButton.required")),
    quantityFirstDelivery: Yup.string().required(t("SendButton.required")),
    quantitySeries: Yup.string().required(t("SendButton.required")),
    quantityAnnual: Yup.string().required(t("SendButton.required")),
    desiredDeliveryTime: Yup.string().required(t("SendButton.required")),
  });

  const handleRequestError = (err) => {
    onError(err);
  };

  // function to be calles when user opens modal
  const onButtonClick = () => {
    configRequestSentGA(partRef.current);
    setShow(true);
  };

  // function to be calles when user confirms modal
  const onConfirm = () => {
    if (!state.disableConfirmButton) {
      setIsSubmit(true);
    }
  };

  // function to be called when user cancels or closes modal
  const onClose = () => {
    setShow(false);
  };

  const sendMail = (data) => {
    dispatch({ type: "toggleMailIsPending" });

    // gather request options
    const requestOptions = {
      method: "POST",
      headers: {
        "Content-type": "application/json",
        "x-api-key": window.env.REACT_APP_BACKEND_API_KEY,
      },
      body: JSON.stringify(data),
    };

    const apiUrl =
      process.env.NODE_ENV === "production" ? "/" : "http://localhost:9001/";
    // make the fetch request to the backend
    fetch(`${apiUrl}api/v1/`, requestOptions)
      .then(async (response) => {
        if (response.ok && response.status === 200) {
          dispatch({ type: "toggleMailIsPending" });
          setShow(false);
          navigate("/confirm");
        } else if (!response.ok && response.status === 401) {
          throw new Error("Authorisierung fehlgeschlagen");
        } else {
          throw new Error(response.status, response.statusText);
        }
      })
      .catch((err) => {
        dispatch({ type: "toggleMailIsPending" });
        setShow(false);
        handleRequestError(err);
      });
  };

  useEffect(() => {
    if (show) {
      const ref = overlayRef.current;
      const element = ref.shadowRoot.querySelector(".backdrop");
      const content = ref.shadowRoot.querySelector(".content");

      if (locationState) {
        // save key to local storage to persist data
        setLatestConfig([locationState, "/summary"]);
      }

      if (content) {
        content.style.zIndex = "9999";
      } else {
        setShow(false);
        setTimeout(() => {
          setShow(true);
        }, 1000);
      }

      if (element !== null) {
        element.addEventListener("click", onClose, false);

        return () => {
          element.removeEventListener("click", onClose, true);
        };
      }
    }
  }, [show, locationState]);

  useEffect(() => {
    if (part !== undefined) {
      partRef.current = part;
    }
  }, [part]);

  useEffect(() => {
    if (data !== undefined) {
      dataRef.current = data;
    }
  }, [data]);

  useEffect(() => {
    // get values from formik after submit
    if (values !== null) {
      offerRequestSentGA(
        values.intendedUse,
        values.quantityFirstDelivery,
        values.quantitySeries,
        values.quantityAnnual
      );

      const countries =
        i18n.language === "de-DE" || i18n.language === "de"
          ? countriesDe
          : countriesEn;
      const countryCode = (element) => element.alpha2 === values.country;
      const country = countries.find(countryCode);

      const data = {
        customer: {
          company: values.company,
          contactPerson: values.firstName + " " + values.lastName,
          email: values.email,
          phone: values.phone,
          zip: values.zip,
          city: values.city,
          street: values.street,
          country: country ? country.name : values.country,
          intendedUse: values.intendedUse,
          inContact: values.inContact,
        },
        product: {
          key: partRef.current,
          info: productInformation, // some product info to display in the mails (send by the backend)
        },
        delivery: {
          quantityFirstDelivery: values.quantityFirstDelivery,
          quantitySeries: values.quantitySeries,
          quantityAnnual: values.quantityAnnual,
          desiredDeliveryTime: values.desiredDeliveryTime,
        },
        annotation: values.annotation,
      };
      sendMail(data);
    }
  }, [values]);

  // set listener to get consent status from outside react (on inital render)
  useEffect(() => {
    const onGetConsentStatus = (event) => {
      setIsConsentSet(event.detail["isConsentSet"]);
    };
    window.addEventListener("hyzGetConsentStatus", onGetConsentStatus);
    return () => {
      window.removeEventListener("hyzGetConsentStatus", onGetConsentStatus);
    };
  }, []);

  // request consent status for saving contact data
  useEffect(() => {
    const event = new CustomEvent("hyzRequestConsentStatus", {
      bubbles: true,
    });
    window.dispatchEvent(event);
  }, [showModal, show]);

  return (
    <>
      <PatternlibOverlayContainer
        target="center"
        show={show}
        disable-click-handlers
        ref={overlayRef}
      >
        <PatternlibButton
          onClick={onButtonClick}
          label={t("SendButton.requestOffer")}
          size="small"
          slot="element"
          display="block"
          width="100%"
        ></PatternlibButton>
        <div slot="content" className={styles.modalContent}>
          <div className={styles.modalHeader}>
            <div>
              <h2>{t("SendButton.offerModalTitle")}</h2>
              {productInformation && (
                <>
                  <Separator space="s"></Separator>
                  <h4 className={styles.key}>
                    {productInformation["P"].current}
                  </h4>
                  <Separator space="xxs"></Separator>
                </>
              )}
            </div>
            <div className={styles.closeIcon}>
              <PatternlibIcon icon-name="x" onClick={onClose}></PatternlibIcon>
            </div>
          </div>
          <div className={styles.modalContainer}>
            <div className={styles.modalWrapper}>
              <Formik
                initialValues={{
                  firstName: user["firstName"] ?? "",
                  lastName: user["lastName"] ?? "",
                  company: user["company"] ?? "",
                  email: user["email"] ?? "",
                  phone: user["phone"] ?? "",
                  zip: user["zip"] ?? "",
                  city: user["city"] ?? "",
                  street: user["street"] ?? "",
                  country: user["country"] ?? "",
                  intendedUse: "",
                  quantityFirstDelivery: "0",
                  quantitySeries: "0",
                  quantityAnnual: "0",
                  annotation: "",
                  consent: "",
                  desiredDeliveryTime: "",
                  inContact: user["inContact"] ? user["inContact"] : "",
                }}
                validationSchema={ValidationSchema}
                validate={() => {
                  setIsSubmit(false);
                }}
                onSubmit={(values) => {
                  setIsSubmit(false);
                  setValues(values);

                  // save user data in local storage if consent is set
                  const userData = {
                    firstName: values.firstName,
                    lastName: values.lastName,
                    company: values.company,
                    email: values.email,
                    phone: values.phone,
                    zip: values.zip,
                    city: values.city,
                    street: values.street,
                    country: values.country,
                    inContact: values.inContact,
                  };

                  if (isConsentSet || values.consent === "checked") {
                    setUser(userData);
                  }

                  // if checked, set consent
                  if (values.consent === "checked") {
                    // reuest consent to get set
                    const event = new CustomEvent("hyzRequestSetConsent", {
                      bubbles: true,
                    });
                    window.dispatchEvent(event);
                  }
                }}
              >
                {({ errors, touched, handleChange, values, setFieldValue }) => (
                  <Form>
                    {!auth.isAuthenticated ? (
                      <>
                        <div className={styles.buttonWrapper}>
                          <PatternlibButton
                            onClick={() => {
                              auth.signinRedirect();
                            }}
                            label={t("login")}
                            type="secondary"
                            size="small"
                            icon-position="right"
                          >
                            <PatternlibIcon
                              icon-name="exit"
                              size="24px"
                              slot="icon"
                              bold
                            />
                          </PatternlibButton>
                        </div>
                        <Separator space="xxs"></Separator>
                        <div className={styles.info}>
                          <PatternlibIcon
                            icon-name="info-circle"
                            size="24px"
                          ></PatternlibIcon>
                          {t("SendButton.myLiebherrLogin")}
                        </div>
                        <Separator space="m"></Separator>
                      </>
                    ) : (
                      <>
                        <div className={styles.buttonWrapper}>
                          <PatternlibButton
                            onClick={() => {
                              removeUser(auth.removeUser, user, setFieldValue);
                            }}
                            label={t("logout")}
                            type="secondary"
                            size="small"
                            icon-position="right"
                          >
                            <PatternlibIcon
                              icon-name="exit"
                              size="24px"
                              slot="icon"
                              bold
                            />
                          </PatternlibButton>
                        </div>
                        <Separator space="xxs"></Separator>
                        <div className={styles.info}>
                          <PatternlibIcon
                            icon-name="info-circle"
                            size="24px"
                          ></PatternlibIcon>
                          {t("SendButton.myLiebherrLogout")}
                        </div>
                        <Separator space="m"></Separator>
                      </>
                    )}
                    <h3>{t("SendButton.general")}</h3>
                    <Separator space="m"></Separator>
                    <FormTextinput
                      label={t("SendButton.firstNameLabel")}
                      name="firstName"
                      placeholder={t("SendButton.firstNamePlaceholder")}
                      initialValue={values.firstName}
                      onChange={handleChange("firstName")}
                      error={
                        errors.firstName && touched.firstName
                          ? errors.firstName
                          : null
                      }
                    ></FormTextinput>
                    <Separator></Separator>
                    <FormTextinput
                      label={t("SendButton.lastNameLabel")}
                      name="lastName"
                      placeholder={t("SendButton.lastNamePlaceholder")}
                      initialValue={values.lastName}
                      onChange={handleChange("lastName")}
                      error={
                        errors.lastName && touched.lastName
                          ? errors.lastName
                          : null
                      }
                    ></FormTextinput>
                    <Separator></Separator>
                    <FormTextinput
                      label={t("SendButton.companyLabel")}
                      name="company"
                      placeholder={t("SendButton.companyPlaceholder")}
                      initialValue={values.company}
                      onChange={handleChange("company")}
                      error={
                        errors.company && touched.company
                          ? errors.company
                          : null
                      }
                    ></FormTextinput>
                    <Separator></Separator>
                    <FormTextinput
                      label={t("SendButton.emailLabel")}
                      name="email"
                      placeholder={t("SendButton.emailPlaceholder")}
                      initialValue={values.email}
                      onChange={handleChange("email")}
                      error={
                        errors.email && touched.email ? errors.email : null
                      }
                      type="email"
                    ></FormTextinput>
                    <Separator></Separator>
                    <FormTextinput
                      label={t("SendButton.phoneLabel")}
                      name="phone"
                      placeholder={t("SendButton.phonePlaceholder")}
                      initialValue={values.phone}
                      onChange={handleChange("phone")}
                      error={
                        errors.phone && touched.phone ? errors.phone : null
                      }
                    ></FormTextinput>
                    <Separator></Separator>
                    <FormTextinput
                      label={t("SendButton.zipLabel")}
                      name="zip"
                      placeholder={t("SendButton.zipPlaceholder")}
                      initialValue={values.zip}
                      onChange={handleChange("zip")}
                      error={errors.zip && touched.zip ? errors.zip : null}
                    ></FormTextinput>
                    <Separator></Separator>
                    <FormTextinput
                      label={t("SendButton.cityLabel")}
                      name="city"
                      placeholder={t("SendButton.cityPlaceholder")}
                      initialValue={values.city}
                      onChange={handleChange("city")}
                      error={errors.city && touched.city ? errors.city : null}
                    ></FormTextinput>
                    <Separator></Separator>
                    <FormTextinput
                      label={t("SendButton.streetLabel")}
                      name="street"
                      placeholder={t("SendButton.streetPlaceholder")}
                      initialValue={values.street}
                      onChange={handleChange("street")}
                      error={
                        errors.street && touched.street ? errors.street : null
                      }
                    ></FormTextinput>
                    <Separator></Separator>
                    <FormDropdown
                      label={t("SendButton.countryLabel")}
                      name="country"
                      placeholder={t("SendButton.countryPlaceholder")}
                      initialValue={values.country}
                      data={
                        i18n.language === "de-DE" || i18n.language === "de"
                          ? countriesDe
                          : countriesEn
                      }
                      isCountry={true}
                      onChange={handleChange("country")}
                      error={
                        errors.country && touched.country
                          ? errors.country
                          : null
                      }
                    ></FormDropdown>
                    <Separator></Separator>
                    <FormTextarea
                      label={t("SendButton.usageLabel")}
                      name="intendedUse"
                      placeholder={t("SendButton.usagePlaceholder")}
                      initialValue={values.intendedUse}
                      onChange={handleChange("intendedUse")}
                      additionalInfo={t("SendButton.usageInfo")}
                    ></FormTextarea>
                    <Separator space="m"></Separator>
                    <h3>{t("SendButton.delivery")}</h3>
                    <Separator space="m"></Separator>
                    <FormTextinput
                      label={t("SendButton.firstDelivery")}
                      name="quantityFirstDelivery"
                      placeholder={t("SendButton.enterQuantity")}
                      initialValue={values.quantityFirstDelivery}
                      onChange={handleChange("quantityFirstDelivery")}
                      error={
                        errors.quantityFirstDelivery &&
                        touched.quantityFirstDelivery
                          ? errors.quantityFirstDelivery
                          : null
                      }
                      type="number"
                      min="0"
                    ></FormTextinput>
                    <Separator></Separator>
                    <FormTextinput
                      label={t("SendButton.quantitySeries")}
                      name="quantitySeries"
                      placeholder={t("SendButton.enterQuantity")}
                      initialValue={values.quantitySeries}
                      onChange={handleChange("quantitySeries")}
                      error={
                        errors.quantitySeries && touched.quantitySeries
                          ? errors.quantitySeries
                          : null
                      }
                      type="number"
                      min="0"
                    ></FormTextinput>
                    <Separator></Separator>
                    <FormTextinput
                      label={t("SendButton.quantityAnnual")}
                      name="quantityAnnual"
                      placeholder={t("SendButton.enterQuantity")}
                      initialValue={values.quantityAnnual}
                      onChange={handleChange("quantityAnnual")}
                      error={
                        errors.quantityAnnual && touched.quantityAnnual
                          ? errors.quantityAnnual
                          : null
                      }
                      type="number"
                      min="0"
                    ></FormTextinput>
                    <Separator></Separator>
                    <FormDatepicker
                      label={t("SendButton.deliveryTimeLabel")}
                      name="desiredDeliveryTime"
                      placeholder={t("SendButton.deliveryTimePlaceholder")}
                      onChange={handleChange("desiredDeliveryTime")}
                      error={
                        errors.desiredDeliveryTime &&
                        touched.desiredDeliveryTime
                          ? errors.desiredDeliveryTime
                          : null
                      }
                    ></FormDatepicker>
                    <Separator space="m"></Separator>
                    <h3>{t("SendButton.annotation")}</h3>
                    <Separator space="m"></Separator>
                    <FormTextarea
                      label={t("SendButton.annotationLabel")}
                      name="annotation"
                      placeholder={t("SendButton.annotationPlaceholder")}
                      initialValue={values.annotation}
                      onChange={handleChange("annotation")}
                    ></FormTextarea>
                    <Separator></Separator>
                    <FormCheckbox
                      label={t("SendButton.contactQuestion")}
                      initialValue={values.inContact}
                      onChange={handleChange("inContact")}
                    ></FormCheckbox>
                    {isConsentSet && <Separator space="m"></Separator>}
                    {/* if consent is not set show checkbox */}
                    {!isConsentSet && (
                      <>
                        <Separator space="m"></Separator>
                        <FormCheckbox
                          label={t("SendButton.consentSavingContactData")}
                          initialValue={values.consent}
                          onChange={handleChange("consent")}
                        ></FormCheckbox>
                        <Separator space="xs"></Separator>
                        <span>
                          {t("SendButton.consentSavingContactDataDesc")}
                        </span>
                        <Separator space="m"></Separator>
                      </>
                    )}
                    {/* adding spinner with two separators, removing the first if the spinner is shown (to make sure the spinner is seen in full heigth) */}
                    {!state.showSpinner && <Separator space="m"></Separator>}
                    {state.showSpinner && (
                      <PatternlibSpinner spin></PatternlibSpinner>
                    )}
                    <Separator space="m"></Separator>
                    <GetValuesFromFormik />
                  </Form>
                )}
              </Formik>
            </div>
          </div>
          <div>
            {isRequired && (
              <>
                <div className={styles.required}>
                  <PatternlibIcon
                    icon-name="triangle-with-exclamation-mark"
                    size="24px"
                    color={"var(--color-warning)"}
                  ></PatternlibIcon>
                  <span>{t("SendButton.required")}</span>
                  <Separator space="xxxxxs"></Separator>
                </div>
              </>
            )}
            <div className={styles.modalFooter}>
              <PatternlibTextlink
                label={t("SendButton.offerModalCancel")}
                size="small"
                onClick={onClose}
              ></PatternlibTextlink>
              <PatternlibButton
                label-text-align="left"
                icon-position="right"
                label={t("SendButton.offerModalConfirm")}
                size="small"
                disabled={state.disableConfirmButton}
                onClick={onConfirm}
              >
                <PatternlibIcon
                  icon-name="font-arrow"
                  slot="icon"
                ></PatternlibIcon>
              </PatternlibButton>
            </div>
          </div>
        </div>
      </PatternlibOverlayContainer>
    </>
  );
};

SendButton.propTypes = {
  part: PropTypes.string,
  data: PropTypes.any,
  onError: PropTypes.func.isRequired,
  showModal: PropTypes.bool,
  locationState: PropTypes.object,
  userData: PropTypes.object,
};

SendButton.defaultProps = {
  part: "",
  data: null,
  showModal: false,
};

export default SendButton;
